<template>
  <v-container class="order-page" style="min-height: 2400px;">
  <v-row>
    <v-col cols="12" md="8">

    <!-- Tabellen: Komplettpaket + Einzelauswahl -->
    <section class="order-section mt-6">
      <h3 class="text-green">Für Zahnärzte kostenlos</h3>
      <h2>
        Jetzt WaizmannTabellen
        <span class="text-green">{{ baseStore.thisYear }}</span> anfordern
      </h2>

      <v-expand-transition>
        <div v-if="lastOrder" ref="confirmRef" class="order-confirm">
          <div class="order-confirm__head">
            <v-icon color="green" size="28">mdi-check-circle</v-icon>
            <div class="order-confirm__body">
              <p class="order-confirm__title">Vielen Dank – Ihre Anforderung ist eingegangen.</p>
              <p class="order-confirm__text">Wir senden Ihnen kostenlos zu:</p>
              <div class="selected-products-card__chips mt-2">
                <v-chip
                  v-for="item in lastOrder.items"
                  :key="item"
                  class="selected-chip"
                  color="green"
                  variant="tonal"
                  size="small"
                >
                  {{ item }}
                </v-chip>
              </div>
              <p class="table-row__caption mt-2">
                Die Anforderung steht rechts unter „Von Ihnen angeforderte WaizmannTabellen“.
                Sie können direkt weitere Tabellen anfordern.
              </p>
            </div>
            <v-btn icon variant="text" size="small" aria-label="Schließen" @click="lastOrder = null">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <div v-if="showPzrTeaser" class="order-confirm__teaser">
            <TableTile size="sm" v-bind="tileProps('pzr')" />
            <div class="order-confirm__teaser-body">
              <p class="order-confirm__teaser-title">Tipp für Ihren Prophylaxe-Bereich</p>
              <p class="order-confirm__teaser-text">
                Die WaizmannTabelle Prophylaxe mit den Top-Tarifen mit PZR-Flatrate – ideal am Prophylaxe-Platz,
                gerne mit passendem Design-Aufsteller.
              </p>
            </div>
            <v-btn color="primary" variant="flat" class="bundle-card__button" @click="teaserPzr">
              Prophylaxe-Tabelle dazu wählen
            </v-btn>
          </div>
        </div>
      </v-expand-transition>

      <div
        :class="['bundle-card', 'product-selection', 'my-4', { 'bundle-card--active': packageActive }]"
        role="button"
        tabindex="0"
        @click="togglePackage"
        @keydown.enter.prevent="togglePackage"
      >
        <div class="bundle-card__body">
          <p class="eyebrow">Unsere Empfehlung</p>
          <h3 class="bundle-card__title">
            WaizmannTabelle Komplettpaket <sup class="text-green table-row__badge">Neu</sup>
          </h3>
          <p class="bundle-card__text">
            Erwachsene, 60plus und KFO/Kind in einem Paket: die drei WaizmannTabellen für alle
            Patientengruppen Ihrer Praxis – im praktischen Postkartenformat zum Mitnehmen.
            <b class="bundle-card__new">Neu dabei: die WaizmannTabelle 60plus</b> mit Tarifen, die bis über
            <strong>GOZ-Faktor 5</strong> leisten.
          </p>
          <v-switch
            color="green"
            inset
            hide-details
            density="compact"
            class="mt-2"
            label="Komplettpaket kostenlos anfordern"
            :model-value="packageActive"
            @click.stop
            @update:modelValue="togglePackage"
          ></v-switch>
          <v-expand-transition>
            <div v-if="packageActive" class="stand-addon" @click.stop>
              <p class="stand-addon__label">Passenden Design-Aufsteller gleich dazu?</p>
              <div class="stand-addon__items">
                <div
                  v-for="stand in standsFor(packageStandKeys)"
                  :key="stand.id"
                  :class="['stand-addon__item', { 'stand-addon__item--active': isProductSelected(stand.key) }]"
                  @click.stop="toggleStand(stand)"
                >
                  <img :src="stand.image" :alt="stand.title" class="stand-addon__img" />
                  <span class="stand-addon__title">
                    {{ stand.title }}
                    <sup v-if="stand.badge" class="text-green table-row__badge">{{ stand.badge }}</sup>
                  </span>
                  <v-switch
                    color="green"
                    inset
                    hide-details
                    density="compact"
                    :model-value="isProductSelected(stand.key)"
                    @click.stop
                    @update:modelValue="() => toggleStand(stand)"
                  ></v-switch>
                </div>
              </div>
            </div>
          </v-expand-transition>
          <v-expand-transition>
            <div v-if="packageActive" class="bundle-card__cta" @click.stop>
              <v-btn
                color="primary"
                size="large"
                class="bundle-card__button"
                @click.stop="startNeueBestellung"
              >
                <v-icon start>play_arrow</v-icon>
                {{ orderButtonLabel }}
              </v-btn>
              <p class="table-row__caption">
                Versand an {{ userData?.user_name }}, {{ userData?.user_plz }} {{ userData?.user_ort }} –
                <a @click.stop="showUserUpdate = !showUserUpdate">Adresse ändern</a>
              </p>
            </div>
          </v-expand-transition>
        </div>
        <div class="bundle-card__tiles" aria-hidden="true">
          <TableTile
            v-for="id in packageTileIds"
            :key="id"
            size="sm"
            v-bind="tileProps(id)"
          />
        </div>
      </div>

      <p class="order-section__eyebrow mt-6">
        {{ packageActive ? 'Speziell für Ihren Prophylaxe-Bereich' : 'Oder einzeln wählen' }}
      </p>
      <p v-if="packageActive" class="order-section__subtitle">
        Ergänzen Sie Ihr Komplettpaket um die Prophylaxe-Tabelle – ideal am Prophylaxe-Platz,
        gerne mit dem passenden Design-Aufsteller.
      </p>

      <div
        v-for="table in visibleTables"
        :key="table.id"
        :class="['table-row', 'product-selection', 'my-4', { 'table-row--active': isTableActive(table.key), 'table-row--highlight': packageActive && table.id === 'pzr' && !isTableActive(table.key) }]"
        :data-table-id="table.id"
        role="button"
        tabindex="0"
        @click="toggleTable(table.key)"
        @keydown.enter.prevent="toggleTable(table.key)"
      >
        <img :src="table.image" :alt="table.title" class="table-row__media" />
        <div class="table-row__body">
          <h3 class="table-row__title">
            {{ table.title }}
            <sup v-if="packageActive && table.id === 'pzr'" class="text-green table-row__badge">Passt zum Paket</sup>
            <sup v-else-if="table.badge" class="text-green">{{ table.badge }}</sup>
          </h3>
          <p class="table-row__text" v-html="table.description"></p>
          <v-switch
            color="green"
            inset
            hide-details
            density="compact"
            class="mt-2"
            :label="table.switchLabel"
            :model-value="isTableActive(table.key)"
            @click.stop
            @update:modelValue="() => toggleTable(table.key)"
          ></v-switch>
          <p v-if="packageActive && isPackageMember(table.key)" class="table-row__caption">
            Im Komplettpaket enthalten – dazu passt der 3-fach Design-Aufsteller oben beim Paket.
          </p>
          <v-expand-transition>
            <div v-if="isTableActive(table.key) && !(packageActive && isPackageMember(table.key))" class="stand-addon" @click.stop>
              <p class="stand-addon__label">Passenden Design-Aufsteller gleich dazu?</p>
              <div class="stand-addon__items">
                <div
                  v-for="stand in standsFor(table.standKeys)"
                  :key="stand.id"
                  :class="['stand-addon__item', { 'stand-addon__item--active': isProductSelected(stand.key) }]"
                  @click.stop="toggleStand(stand)"
                >
                  <img :src="stand.image" :alt="stand.title" class="stand-addon__img" />
                  <span class="stand-addon__title">
                    {{ stand.title }}
                    <sup v-if="stand.badge" class="text-green table-row__badge">{{ stand.badge }}</sup>
                  </span>
                  <v-switch
                    color="green"
                    inset
                    hide-details
                    density="compact"
                    :model-value="isProductSelected(stand.key)"
                    @click.stop
                    @update:modelValue="() => toggleStand(stand)"
                  ></v-switch>
                </div>
              </div>
            </div>
          </v-expand-transition>
          <v-expand-transition>
            <div v-if="isTableActive(table.key) && !(packageActive && isPackageMember(table.key))" class="row-cta" @click.stop>
              <v-btn color="primary" size="large" class="bundle-card__button" @click.stop="startNeueBestellung">
                <v-icon start>play_arrow</v-icon>
                {{ orderButtonLabel }}
              </v-btn>
            </div>
          </v-expand-transition>
        </div>
        <div class="table-row__tile">
          <TableTile v-bind="tileProps(table.id)" />
        </div>
      </div>
    </section>

    <!-- Design-Aufsteller (nur die, die nicht schon inline bei einer aktiven Tabelle stehen) -->
    <section v-if="visibleStands.length" class="order-section my-8">
      <p class="order-section__eyebrow">Neu</p>
      <h2 class="order-section__title">Design-Aufsteller</h2>
      <p class="order-section__subtitle">
        Elegante Aufsteller für Empfang und Wartezimmer – kostenlos.
        Alle Aufsteller sind auch einzeln, ohne Tabelle, bestellbar.
      </p>
      <div class="stands-grid">
        <div
          v-for="stand in visibleStands"
          :key="stand.id"
          :class="[
            'stand-card',
            {
              'stand-card--active': isProductSelected(stand.key),
              'stand-card--disabled': !isStandAvailable(stand),
            },
          ]"
          role="button"
          tabindex="0"
          @click="toggleStand(stand)"
          @keydown.enter.prevent="toggleStand(stand)"
        >
          <div class="stand-card__media" :style="{ '--tile-bg': tileProps(stand.tileId).color }">
            <img :src="stand.image" :alt="stand.title" />
          </div>
          <div class="stand-card__content">
            <p class="stand-card__title">
              {{ stand.title }}
              <sup v-if="stand.badge" class="text-green table-row__badge">{{ stand.badge }}</sup>
            </p>
            <p class="stand-card__text">{{ stand.text }}</p>
            <v-switch
              color="green"
              inset
              hide-details
              density="compact"
              class="mt-2"
              :label="stand.switchLabel"
              :disabled="!isStandAvailable(stand)"
              :model-value="isProductSelected(stand.key)"
              @click.stop
              @update:modelValue="() => toggleStand(stand)"
            ></v-switch>
            <p v-if="!isStandAvailable(stand)" class="stand-card__caption">
              {{ stand.requiresHint }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Gesamtausgabe -->
    <section class="order-section my-8">
      <div
        :class="['table-row', 'product-selection', { 'table-row--active': isProductSelected(PRODUCT_KEYS.GESAMT) }]"
        role="button"
        tabindex="0"
        @click="toggleGesamtausgabe"
        @keydown.enter.prevent="toggleGesamtausgabe"
      >
        <img
          :src="gesamtausgabeImage"
          alt="WaizmannTabelle Gesamtausgabe"
          class="table-row__media table-row__media--book"
        />
        <div class="table-row__body">
          <h3 class="table-row__title">
            WaizmannTabelle Gesamtausgabe {{ baseStore.thisYear }}
          </h3>
          <p class="table-row__text">
            Alle getesteten Zahnzusatzversicherungen inklusive PZR-Flatrate-Vergleich, Wechselanleitung
            und Tipps für abrechnungsstarke Tarife. Ideal für den Lesebereich im Wartezimmer.
          </p>
          <v-switch
            color="green"
            inset
            hide-details
            density="compact"
            class="mt-2"
            label="Gesamtausgabe kostenlos anfordern"
            :model-value="isProductSelected(PRODUCT_KEYS.GESAMT)"
            @click.stop
            @update:modelValue="toggleGesamtausgabe"
          ></v-switch>
          <v-expand-transition>
            <div v-if="isProductSelected(PRODUCT_KEYS.GESAMT)" class="row-cta" @click.stop>
              <v-btn color="primary" size="large" class="bundle-card__button" @click.stop="startNeueBestellung">
                <v-icon start>play_arrow</v-icon>
                {{ orderButtonLabel }}
              </v-btn>
            </div>
          </v-expand-transition>
        </div>
        <div class="table-row__tile">
          <TableTile v-bind="tileProps('gesamt')" />
        </div>
      </div>
    </section>

    <section class="order-section mt-8">
    <v-row>
      <v-col cols="12" md="12" class="my-0 py-0">
        <div v-if="!showFormAdress" class="caption pa-4 mb-4 address-request-card">
         <h3><a  @click="show_comment = !show_comment"
            >Sie benötigen eine größere Menge?</a
          ></h3>
          Klicken
          <a @click="show_comment = !show_comment">Sie jetzt hier</a> und
          schreiben Sie uns, wieviel Sie genau benötigen.
         
        <span v-if="show_comment">
          <br /><br />
          <v-textarea
            label="Welche Menge benötigen Sie?"
            style="width: 400px;" bg-color="white"
            v-model="bestellung.bestell_comment"
          ></v-textarea>
   
        </span>
        </div>
      
        <v-alert
          v-if="bestellung.bestell_produkt_text == ''"
          color="orange-darken-2"
          variant="tonal"
          border="start"
          class="hint-alert mb-4"
        >
          <strong>Hinweis:</strong> Bitte wählen Sie die gewünschten Tabellen, um mit der Bestellung fortzufahren.
        </v-alert>
        <div v-if="showSuccessUpdate" class="my-4">
          <h3 class="text-green">Ihre Adresse wurde aktualisiert - jetzt können Sie die WaizmannTabellen anfordern</h3>
        </div>
        <div class="my-4"  v-if="bestellung.bestell_produkt_text != '' && showFormAdress">
          <v-row >
            <v-col cols="12"><h3>Bitte ergänzen Sie noch Ihre Adresse</h3></v-col>
          </v-row>
           
  
          <v-row>
            <v-col cols="12" md="9">
            
              <v-text-field
                v-model="updateuserData.user_strasse"
                label="Strasse"
                type="char"
                required
              ></v-text-field
            ></v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="updateuserData.user_hsnr"
                label="Hausnummer"
                type="char"
                required
              ></v-text-field
            ></v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="updateuserData.user_plz"
                label="PLZ"
                type="char"
                required
              ></v-text-field
            ></v-col>
            <v-col cols="12" md="9">
              <v-text-field
                v-model="updateuserData.user_ort"
                label="Ort"
                type="char"
                required
              ></v-text-field
            ></v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="9">
              <v-btn color="primary" @click="updateUserAddress">
                jetzt Adresse aktualisieren
              </v-btn>
            </v-col>
            <v-col cols="12" md="3">
           
            </v-col>
          </v-row>
        </div>
        <h3 class="text-red my-4" v-if="userData && (userData.user_strasse == '' || userData.user_plz == '' || userData.user_ort == '')">Bitte speichern Sie noch Ihre Adresse <br><a @click="showUserUpdate = !showUserUpdate">hier klicken um Adresse zu speichern</a></h3>        

        <div
          v-if="bestellung.bestell_produkt_text != ''"
          class="selected-products-card mb-4"
        >
          <p class="eyebrow eyebrow--normal mb-1">Ihre gewählten WaizmannTabellen</p>
      
          <div class="selected-products-card__chips">
            <v-chip
              v-for="produkt in getProduktListe()"
              :key="produkt"
              class="selected-chip"
              color="green"
              variant="tonal"
              size="small"
            >
              {{ produkt }}
            </v-chip>
          </div>
        </div>

        <v-btn
          v-if="bestellung.bestell_produkt_text != '' && !showFormAdress"
          color="primary"
          style="height: 48px; font-size: 16px"
          elevated
          tile
          @click="startNeueBestellung"
          type="submit"
          class="button_caps"
          tonal
        >
          <span class="white--text"   v-if="baseStore.isDesktop">

         
            <v-icon style="font-size: 28px">play_arrow</v-icon> jetzt<span
              >&nbsp;kostenlos neue&nbsp;</span
            >WaizmannTabellen anfordern
          </span>
          <span class="white--text"   v-if="!baseStore.isDesktop">
            <v-icon style="font-size: 28px">play_arrow</v-icon> jetzt kostenlos anfordern
          </span>
        </v-btn>
        <div
          class="py-4 mt-4 address-check-card"
          v-if="bestellung.bestell_produkt_text != '' && !showFormAdress"
        >
          <h2 class="text-green">Ist Ihre Adresse noch korrekt?</h2>
          <ul>
            <li>{{ userData.user_name }}</li>
            <li v-if="userData.user_strasse != ''">{{ userData.user_strasse }} {{ userData.user_hsnr }}</li>
            <li v-else><b>Bitte speichern Sie noch Ihre Strasse und Hausnummer</b></li>
            <li v-if="userData.user_ort != ''">{{ userData.user_plz }} {{ userData.user_ort }}</li>
            <li v-else><b>Bitte speichern Sie noch Ihre PLZ und Ort</b></li>
         <li>
              <a @click="showUserUpdate = !showUserUpdate">Adresse aktualisieren</a>
            </li>
          </ul>
                </div>
      </v-col>
    </v-row>
    </section>

    </v-col>
    <v-col cols="12" md="4" v-if="baseStore.isDesktop" class="address-sidebar">
      <div v-if="bestellung.bestell_produkt_text != ''" class="address-card address-card--fixed mt-12">
        <p class="eyebrow">Versandadresse</p>
        <h3>Wohin sollen wir Ihre WaizmannTabellen schicken?</h3>
        <ul class="address-list">
          <li>{{ userData.user_name }}</li>
          <li v-if="userData.user_strasse != ''">{{ userData.user_strasse }} {{ userData.user_hsnr }}</li>
          <li v-else><b>Bitte speichern Sie noch Ihre Strasse und Hausnummer</b></li>
          <li v-if="userData.user_ort != ''">{{ userData.user_plz }} {{ userData.user_ort }}</li>
          <li v-else><b>Bitte speichern Sie noch Ihre PLZ und Ort</b></li>
        </ul>

        <div class="address-actions">
          <v-btn variant="text" color="primary" @click="showUserUpdate = !showUserUpdate">
            Adresse aktualisieren
          </v-btn>
          <v-btn color="primary" @click="startNeueBestellung">
            <v-icon start>play_arrow</v-icon>
            Jetzt kostenlos anfordern
          </v-btn>
        </div>
        <p
          class="text-red mt-4"
          v-if="userData && (userData.user_strasse == '' || userData.user_plz == '' || userData.user_ort == '')"
        >
          Bitte speichern Sie noch Ihre Adresse.
        </p>
        <div
          v-if="bestellung.bestell_produkt_text != ''"
          class="selected-products-card mt-4"
        >
          <p class="eyebrow eyebrow--normal mb-1">Ihre gewählten WaizmannTabellen</p>
      
          <div class="selected-products-card__chips">
            <v-chip
              v-for="produkt in getProduktListe()"
              :key="produkt"
              class="selected-chip"
              color="green"
              variant="tonal"
              size="small"
            >
              {{ produkt }}
            </v-chip>
          </div>
        </div>
      </div>

      <template v-if="bestellungen && bestellungen.length > 0 && bestellung.bestell_produkt_text == ''">
        <p class="eyebrow mt-6">Von Ihnen angeforderte</p>
        <h2 class="order-history__title">WaizmannTabellen</h2>
        <div
          v-for="best in bestellungen"
          :key="best.bestell_id"
          class="order-item"
        >
          <div class="order-item__header">
            <span class="order-item__date">{{ best.datumBestellung }}</span>
            <span class="status-chip" :class="best.statusClass">{{ best.status }}</span>
          </div>
          <p class="order-item__title">
            {{ best.bestell_produkt_text || 'WaizmannTabellen' }}
          </p>
          <p v-if="best.bestell_comment" class="order-item__comment">
            {{ best.bestell_comment }}
          </p>
        </div>
      </template>
    </v-col>
  </v-row>
  <v-dialog v-model="showUserUpdate" width="888">
    <v-card>
      <v-card-text >
        <AccountView  />
      </v-card-text>
    </v-card>
  </v-dialog>
  </v-container>
</template>

<script setup>
import { inject, ref, onMounted, reactive, computed, watch, nextTick } from 'vue'
import { storeToRefs } from 'pinia';
import AccountView from '@/components/objects/user/AccountView.vue'
import TableTile from '@/components/sites/objects/TableTile.vue'
import { PRODUCT_KEYS, PACKAGE_MEMBERS, PRODUCT_ORDER } from '@/constants/bestellProdukte'

const baseCalls = inject('baseCalls')
const loaderStore = inject('loaderStore');
const baseStore = inject('baseStore')
const errorStore = inject('errorStore');
const userStore = inject('userStore')
const { userData } = storeToRefs(userStore);
const updateDocumentHead = inject('updateDocumentHead');
const showSuccessUpdate = ref(false);
const showUserUpdate = ref(false);

const bestellungen = ref([])
const lastOrder = ref(null); // { items: [...] } – Inline-Bestätigung der letzten Anforderung
const confirmRef = ref(null);
const show_comment = ref(false);
const showFormAdress = ref(false);

// ---------------------------------------------------------------------------
// Kacheln (Nummer + Farbe) kommen aus baseStore.waizmannTableTiles
// ---------------------------------------------------------------------------
const packageTileIds = ['erwachsene', '60plus', 'kinder'];

function tileProps(id) {
  const tiles = Array.isArray(baseStore.waizmannTableTiles) ? baseStore.waizmannTableTiles : [];
  const tile = tiles.find((item) => item.id === id) || {};
  return {
    number: tile.number || '',
    subtitle: tile.label || tile.subtitle || '',
    color: tile.color || '#3166a5',
    ink: tile.ink || 'light',
  };
}

// ---------------------------------------------------------------------------
// Bilder
// ---------------------------------------------------------------------------
const tableImages = {
  erwachsene: new URL('../../../assets/bestellung/togo_e_picto.png', import.meta.url).href,
  '60plus': new URL('../../../assets/bestellung/togo_60plus_picto.png', import.meta.url).href,
  kinder: new URL('../../../assets/bestellung/togo_k_picto.png', import.meta.url).href,
  pzr: new URL('../../../assets/bestellung/togo_pzr_picto.png', import.meta.url).href,
};

const gesamtausgabeImage = new URL('../../../assets/bestellung/gesamtausgabe.png', import.meta.url).href;

// 3-fach: Foto; KFO/Kind und Prophylaxe: gerenderte Illustrationen (bei neuen Fotos hier tauschen)
const standImages = {
  triple: new URL('../../../assets/bestellung/dreifachaufsteller.jpg', import.meta.url).href,
  kinder: new URL('../../../assets/bestellung/aufsteller_1fach_kinder.png', import.meta.url).href,
  pzr: new URL('../../../assets/bestellung/aufsteller_1fach_pzr.png', import.meta.url).href,
};

// ---------------------------------------------------------------------------
// Produkte
// ---------------------------------------------------------------------------
const tables = [
  {
    id: 'erwachsene',
    key: PRODUCT_KEYS.ADULT,
    title: 'WaizmannTabelle Erwachsene',
    description: 'Die Top-Tarife für Erwachsene im praktischen Postkartenformat – zum Mitnehmen für Ihr Wartezimmer.',
    switchLabel: 'Erwachsenen-Tabelle kostenlos anfordern',
    image: tableImages.erwachsene,
    standKeys: [PRODUCT_KEYS.STAND_TRIPLE],
    badge: 'Tipp',
  },
  {
    id: '60plus',
    key: PRODUCT_KEYS.SENIOR,
    title: 'WaizmannTabelle 60plus',
    description: 'Neu: Zahnzusatzversicherungen für Patienten ab 60 – die besten Tarife ohne Altersgrenze, mit starken Zahnersatz-Leistungen und Tarifen, die bis über <strong>GOZ-Faktor 5</strong> leisten.',
    switchLabel: '60plus-Tabelle kostenlos anfordern',
    image: tableImages['60plus'],
    standKeys: [PRODUCT_KEYS.STAND_TRIPLE],
    badge: 'Neu',
  },
  {
    id: 'kinder',
    key: PRODUCT_KEYS.KIDS,
    title: 'WaizmannTabelle KFO/Kind',
    description: 'Die wichtigsten Kindertarife für KFO & Zahnbehandlungen – ideal für die Familienberatung in der Praxis.',
    switchLabel: 'KFO/Kind-Tabelle kostenlos anfordern',
    image: tableImages.kinder,
    standKeys: [PRODUCT_KEYS.STAND_KIDS],
  },
  {
    id: 'pzr',
    key: PRODUCT_KEYS.PZR,
    title: 'WaizmannTabelle Prophylaxe',
    description: 'Top-Tarife mit PZR-Flatrate: unbegrenzte Prophylaxe plus Zahnersatz & Zahnerhalt.',
    switchLabel: 'Prophylaxe-Tabelle kostenlos anfordern',
    image: tableImages.pzr,
    standKeys: [PRODUCT_KEYS.STAND_PZR],
  },
];

const stands = [
  {
    id: 'triple',
    key: PRODUCT_KEYS.STAND_TRIPLE,
    tileId: 'erwachsene',
    title: 'Design-Aufsteller 3-fach',
    badge: 'Neu',
    text: 'Erwachsene, 60plus und KFO/Kind nebeneinander im Wartezimmer. Auch ohne Komplettpaket bestellbar.',
    switchLabel: 'Kostenlos anfordern',
    image: standImages.triple,
    requires: [],
    requiresHint: '',
  },
  {
    id: 'kinder',
    key: PRODUCT_KEYS.STAND_KIDS,
    tileId: 'kinder',
    title: 'Design-Aufsteller KFO/Kind',
    text: 'Passend zur KFO/Kind-Tabelle – für die Familienecke im Wartezimmer. Auch einzeln bestellbar.',
    switchLabel: 'Kostenlos anfordern',
    image: standImages.kinder,
    requires: [],
    requiresHint: '',
  },
  {
    id: 'pzr',
    key: PRODUCT_KEYS.STAND_PZR,
    tileId: 'pzr',
    title: 'Design-Aufsteller Prophylaxe',
    text: 'Passend zur Prophylaxe-Tabelle – ideal am Prophylaxe-Platz. Auch einzeln bestellbar.',
    switchLabel: 'Kostenlos anfordern',
    image: standImages.pzr,
    requires: [],
    requiresHint: '',
  },
];

// ---------------------------------------------------------------------------
// Bestellung + Auswahl
// selected (Produkt-Keys) ist die einzige Quelle; bestell_produkt_text wird daraus abgeleitet.
// ---------------------------------------------------------------------------
const bestellung = reactive({
  bestell_status: 0,
  bestell_comment: "",
  bestell_produkt_text: "",
  bestell_kategorie: 0,
  action: 'neueBestellung',
});

const selected = ref([]);

const isProductSelected = (key) => selected.value.includes(key);
const isPackageMember = (key) => PACKAGE_MEMBERS.includes(key);
const packageActive = computed(() => isProductSelected(PRODUCT_KEYS.PACKAGE));
const isTableActive = (key) => isProductSelected(key) || (packageActive.value && isPackageMember(key));

function getProduktListe() {
  return PRODUCT_ORDER.filter((key) => selected.value.includes(key));
}

function addProduktName(key, was = 'tabelle') {
  if (was === 'webinar') {
    bestellung.bestell_kategorie = 1;
  } else if (was === 'tabelle') {
    bestellung.bestell_kategorie = 0;
  }
  if (!isProductSelected(key)) {
    selected.value = [...selected.value, key];
  }
}

function removeProduktName(key) {
  selected.value = selected.value.filter((item) => item !== key);
}

function toggleProduct(key, was = 'tabelle') {
  if (isProductSelected(key)) {
    removeProduktName(key);
  } else {
    addProduktName(key, was);
  }
}

// Paket dominiert: nie Paket UND Einzelposten gleichzeitig im Produkttext.
function togglePackage() {
  if (packageActive.value) {
    removeProduktName(PRODUCT_KEYS.PACKAGE);
    return;
  }
  PACKAGE_MEMBERS.forEach(removeProduktName);
  removeProduktName(PRODUCT_KEYS.STAND_KIDS);
  addProduktName(PRODUCT_KEYS.PACKAGE, 'tabelle');
}

function toggleTable(key) {
  if (!isPackageMember(key)) {
    toggleProduct(key, 'tabelle');
    return;
  }
  // Abwahl eines Paket-Mitglieds: Paket auflösen, die übrigen einzeln behalten.
  if (packageActive.value) {
    removeProduktName(PRODUCT_KEYS.PACKAGE);
    PACKAGE_MEMBERS.filter((item) => item !== key).forEach((item) => addProduktName(item, 'tabelle'));
    return;
  }
  toggleProduct(key, 'tabelle');
  // Alle drei einzeln gewählt: zum Paket zusammenfassen.
  if (PACKAGE_MEMBERS.every(isProductSelected)) {
    PACKAGE_MEMBERS.forEach(removeProduktName);
    removeProduktName(PRODUCT_KEYS.STAND_KIDS);
    addProduktName(PRODUCT_KEYS.PACKAGE, 'tabelle');
  }
}

const packageStandKeys = [PRODUCT_KEYS.STAND_TRIPLE];

// Bei gewähltem Komplettpaket: Paket-Tabellen und 3-fach nicht noch einmal einzeln anbieten.
const visibleTables = computed(() =>
  packageActive.value ? tables.filter((table) => !isPackageMember(table.key)) : tables
);
// Sektion "Design-Aufsteller":
// - KFO/Kind-Aufsteller entfällt bei Paket (durch den 3-fach abgedeckt).
// - Ein Aufsteller, der inline bei Paket/aktiver Tabelle angeboten wird, bleibt unten sichtbar,
//   solange er noch nicht gewählt ist; sobald er gewählt wurde, verschwindet er unten.
const visibleStands = computed(() =>
  stands.filter((stand) => {
    if (packageActive.value && stand.key === PRODUCT_KEYS.STAND_KIDS) {
      return false;
    }
    const offeredInline =
      (packageActive.value && packageStandKeys.includes(stand.key)) ||
      tables.some(
        (table) =>
          table.standKeys.includes(stand.key) &&
          isTableActive(table.key) &&
          !(packageActive.value && isPackageMember(table.key))
      );
    return !(offeredInline && isProductSelected(stand.key));
  })
);

function standsFor(keys) {
  return stands.filter((stand) => keys.includes(stand.key));
}

function isStandAvailable(stand) {
  return stand.requires.every(isTableActive);
}

function toggleStand(stand) {
  if (!isStandAvailable(stand)) {
    return;
  }
  toggleProduct(stand.key, 'tabelle');
}

// Aufsteller entfernen, deren Tabelle nicht mehr gewählt ist.
function syncDependentStands() {
  stands.forEach((stand) => {
    if (!isStandAvailable(stand) && isProductSelected(stand.key)) {
      removeProduktName(stand.key);
    }
  });
}

function toggleGesamtausgabe() {
  addOderEntferneProdukt(PRODUCT_KEYS.GESAMT, 'tabelle');
}

// Kurznamen für Button-Beschriftungen
const shortNames = {
  [PRODUCT_KEYS.PACKAGE]: 'Komplettpaket',
  [PRODUCT_KEYS.GESAMT]: 'Gesamtausgabe',
  ...Object.fromEntries(tables.map((table) => [table.key, table.title.replace('WaizmannTabelle ', '') + '-Tabelle'])),
  ...Object.fromEntries(stands.map((stand) => [stand.key, stand.title])),
};

const orderButtonLabel = computed(() => {
  const list = getProduktListe();
  if (list.length === 1) {
    return `${shortNames[list[0]] || 'Auswahl'} jetzt kostenlos anfordern`;
  }
  return `Alle ${list.length} Positionen jetzt kostenlos anfordern`;
});

const showPzrTeaser = computed(() =>
  !!lastOrder.value && !lastOrder.value.items.includes(PRODUCT_KEYS.PZR) && !isProductSelected(PRODUCT_KEYS.PZR)
);

function teaserPzr() {
  addProduktName(PRODUCT_KEYS.PZR, 'tabelle');
  nextTick(() => {
    document.querySelector('[data-table-id="pzr"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

function resetSelections() {
  selected.value = [];
}

watch(selected, () => {
  syncDependentStands();
  bestellung.bestell_produkt_text = getProduktListe().join(', ');
});

async function startNeueBestellung()
{
  loaderStore.setLoader(true)
  const response = await baseCalls.bestellungPost(bestellung)
  if (response.data.success) {
    showSuccessUpdate.value = true;
    loaderStore.setLoader(false)
    lastOrder.value = { items: getProduktListe() };
    resetSelections();
    bestellung.bestell_comment = '';
    getBestellungen();
    nextTick(() => {
      confirmRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  } else {
    loaderStore.setLoader(false)
    errorStore.setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.')
  }
}
async function getBestellungen() {
  const response = await baseCalls.bestellungGet({ action: 'getalluserbestellungen', id: 0 })
  if (response.data.success) {
    bestellungen.value = response.data.bestellungen
  }
}

function addOderEntferneProdukt(titel, was) {
  toggleProduct(titel, was);
}


onMounted(() => {
  if (userData.value) {
  getBestellungen()
  }
  setTimeout(() => {
    userStore.setLoginView(false)
  }, 1000);

  updateDocumentHead(
    "WaizmannTabellen anfordern | kostenlos",
    "Jetzt WaizmannTabellen anfordern - kostenlos - für Zahnärzte"
  );
})
</script>

<style scoped>
.mb-4 {
  margin-bottom: 16px;
}

.text-right {
  text-align: right;
}

/* ---------------------------------------------------------------------------
   Farb- und Maß-Tokens der Kacheln
--------------------------------------------------------------------------- */
.order-page {
  --tile-blue: #3166a5;
  --tile-lilac: #6f5aa8;
  --tile-yellow: #f2c14e;
  --tile-pink: #e24b8a;
  --tile-gray: #9aa3af;
  --tile-w: 104px;
}

/* Sektionen */
.order-section {
  background-color: #f7f9fc;
  border-radius: 8px;
  padding: 24px;
}

.order-section__eyebrow {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: #6b7a99;
  font-weight: 600;
  margin: 0 0 6px;
}

.order-section__title {
  font-size: 1.35rem;
  color: #1f3a63;
  margin: 0 0 4px;
}

.order-section__subtitle {
  color: #5a6a85;
  line-height: 1.4;
  margin: 0 0 16px;
}

/* Komplettpaket */
.bundle-card {
  background: #fff;
  border: 1px solid #e1e6ef;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.bundle-card--active {
  border-color: #2e7d32;
  box-shadow: 0 6px 18px rgba(46, 125, 50, 0.15);
}

.bundle-card__body {
  flex: 1;
  min-width: 0;
}

.bundle-card__title {
  color: #3166a5;
  font-size: 1.25rem;
  margin: 0 0 4px;
}

.bundle-card__text {
  margin: 0;
  color: #4a5568;
  line-height: 1.35;
}

.bundle-card__new {
  color: #6f5aa8;
}

.bundle-card__tiles {
  display: flex;
  align-items: flex-end;
  flex: 0 0 auto;
  padding: 8px 6px 4px;
}

.bundle-card__tiles .table-tile {
  box-shadow: 0 8px 20px rgba(16, 42, 67, 0.18);
}

.bundle-card__tiles .table-tile + .table-tile {
  margin-left: -14px;
}

.bundle-card__tiles .table-tile:nth-child(1) {
  transform: rotate(-8deg) translateY(4px);
}

.bundle-card__tiles .table-tile:nth-child(2) {
  transform: translateY(-6px);
  z-index: 1;
}

.bundle-card__tiles .table-tile:nth-child(3) {
  transform: rotate(8deg) translateY(4px);
}

.bundle-card__cta {
  margin-top: 14px;
  cursor: default;
}

.bundle-card__button {
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0.2px;
  max-width: 100%;
  height: auto !important;
  min-height: 44px;
  padding: 8px 16px;
  white-space: normal;
}

.row-cta {
  margin-top: 12px;
  cursor: default;
}

/* Inline-Bestätigung nach einer Anforderung */
.order-confirm {
  background: #fff;
  border: 2px solid #2e7d32;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0 4px;
  box-shadow: 0 8px 24px rgba(46, 125, 50, 0.12);
}

.order-confirm__head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.order-confirm__body {
  flex: 1;
  min-width: 0;
}

.order-confirm__title {
  margin: 2px 0 2px;
  font-weight: 700;
  color: #1f3a63;
  font-size: 1.05rem;
}

.order-confirm__text {
  margin: 0;
  color: #4a5568;
}

.order-confirm__teaser {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 6px;
  border: 1px solid rgba(226, 75, 138, 0.28);
  background: #fdf2f7;
  background: color-mix(in srgb, #e24b8a 8%, #fff);
}

.order-confirm__teaser-body {
  flex: 1 1 240px;
  min-width: 0;
}

.order-confirm__teaser-title {
  margin: 0;
  font-weight: 700;
  color: #1f3a63;
}

.order-confirm__teaser-text {
  margin: 2px 0 0;
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.35;
}

/* Tabellen-Zeile: Picto | Text | Kachel rechts */
.table-row {
  background: #fff;
  border: 1px solid #e1e6ef;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 16px;
  align-items: stretch;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.table-row--highlight {
  border-color: #e24b8a;
  box-shadow: 0 6px 18px rgba(226, 75, 138, 0.14);
}

.table-row--active {
  border-color: #2e7d32;
  box-shadow: 0 6px 18px rgba(46, 125, 50, 0.12);
}

.table-row__media {
  display: none;
  width: 96px;
  height: 96px;
  object-fit: contain;
  flex: 0 0 auto;
  align-self: center;
}

.table-row__media--book {
  height: auto;
}

.table-row__body {
  flex: 1;
  min-width: 0;
}

.table-row__title {
  color: #3166a5;
  margin: 0 0 4px;
}

.table-row__badge {
  white-space: nowrap;
}

.table-row__text {
  margin: 0;
  color: #4a5568;
  line-height: 1.35;
}

.table-row__caption {
  margin: 6px 0 0;
  font-size: 0.75rem;
  color: #6b7a99;
}

.table-row__tile {
  flex: 0 0 var(--tile-w);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
}

/* Aufsteller-Zusatz direkt unter dem aktiven Produkt */
.stand-addon {
  margin-top: 12px;
  padding: 12px;
  border-radius: 6px;
  background: #f7f9fc;
  border: 1px solid #e6ebf5;
  cursor: default;
}

.stand-addon__label {
  margin: 0 0 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f3a63;
}

.stand-addon__items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stand-addon__item {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 240px;
  padding: 6px 10px 6px 6px;
  background: #fff;
  border: 1px dashed #d5ddeb;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.stand-addon__item--active {
  border-style: solid;
  border-color: #2e7d32;
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.12);
}

.stand-addon__img {
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex: 0 0 auto;
}

.stand-addon__title {
  flex: 1;
  min-width: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f3a63;
}

/* Design-Aufsteller */
.stands-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.stand-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px dashed #d5ddeb;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.stand-card--active {
  border-style: solid;
  border-color: #2e7d32;
  box-shadow: 0 6px 18px rgba(46, 125, 50, 0.15);
}

.stand-card--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.stand-card__media {
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: #f7f9fc;
  background: color-mix(in srgb, var(--tile-bg, #3166a5) 12%, #fff);
}

.stand-card__media img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.stand-card__content {
  padding: 14px 16px 16px;
}

.stand-card__title {
  margin: 0;
  font-weight: 600;
  color: #1f3a63;
}

.stand-card__text {
  margin: 2px 0 0;
  color: #4a5568;
  font-size: 0.85rem;
  line-height: 1.35;
}

.stand-card__caption {
  margin: 6px 0 0;
  font-size: 0.75rem;
  color: #b45309;
}

@media (min-width: 600px) {
  .table-row__media {
    display: block;
  }
}

@media (max-width: 959px) {
  .order-page {
    --tile-w: 88px;
  }

  .order-page :deep(.table-tile__title) {
    font-size: 15px;
  }

  .order-page :deep(.table-tile__title--long) {
    font-size: 12px;
  }

  .order-page :deep(.table-tile__title--xlong) {
    font-size: 10px;
  }

  .bundle-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .bundle-card__tiles {
    align-self: center;
  }
}

@media (min-width: 600px) {
  .stands-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 960px) {
  .stands-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.address-card {
  background: #fff;
  border: 1px solid #e1e6ef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 10px 30px rgba(20, 45, 90, 0.05);
}

.address-sidebar {
  position: relative;
  min-height: 600px;
}

.address-card--fixed {
  position: fixed;
  top: 100px;
  right: clamp(16px, 5vw, 96px);
  width: min(360px, calc(100vw - 64px));
  z-index: 5;
}

.address-card__default {
  font-size: 0.9rem;
  color: #4a5568;
  line-height: 1.4;
}

.address-request-card,
.address-check-card {
  text-align: left;
  background-color: #f2f2f2;
  border-radius: 8px;
}

.hint-alert {
  font-size: 1rem;
  line-height: 1.4;
}

.product-selection {
  cursor: pointer;
}

.product-selection:focus-visible {
  outline: 2px solid #3166a5;
  outline-offset: 4px;
  border-radius: 8px;
}

.selected-products-card {
  background: #fff;
  border: 1px solid #e1e6ef;
  border-radius: 8px;
  padding: 16px;
}

.selected-products-card__hint {
  margin: 0 0 12px;
  color: #4a5568;
  font-size: 0.9rem;
}

.selected-products-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-chip {
  border-radius: 6px;
  text-transform: none;
  font-weight: 600;
}

.eyebrow {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: #6b7a99;
  margin-bottom: 4px;
}
.eyebrow--normal {
  text-transform: none;
  letter-spacing: normal;
  font-size: 0.9rem;
}

.address-list {
  list-style: none;
  padding: 0;
  margin: 12px 0 16px;
  color: #1f3a63;
}

.address-list li + li {
  margin-top: 4px;
}

.address-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.order-history__title {
  margin: 0 0 16px;
}

.order-item {
  border: 1px solid #e6ebf5;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
  background: #fdfefe;
}

.order-item__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.85rem;
}

.order-item__title {
  font-weight: 600;
  margin: 0 0 4px;
}

.order-item__comment {
  margin: 0;
  color: #4a5568;
  font-size: 0.9rem;
}

.status-chip {
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.75rem;
  text-transform: uppercase;
}
</style>
